import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { getCallStats } from '../services/aiService';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const QAMetrics = () => {
  const [timeRange, setTimeRange] = useState('week');
  const [metrics, setMetrics] = useState({
    totalCalls: 0,
    completedCalls: 0,
    failedCalls: 0,
    averageDuration: 0,
    statusDistribution: [],
    rawStats: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchMetrics();
  }, [timeRange]);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();
      
      switch (timeRange) {
        case 'week':
          startDate.setDate(endDate.getDate() - 7);
          break;
        case 'month':
          startDate.setMonth(endDate.getMonth() - 1);
          break;
        case 'quarter':
          startDate.setMonth(endDate.getMonth() - 3);
          break;
        default:
          startDate.setDate(endDate.getDate() - 7);
      }

      const params = {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      };

      const response = await getCallStats(params);
      
      if (response.success && response.data) {
        const stats = response.data;
        
        // Process the stats data
        let totalCalls = 0;
        let completedCalls = 0;
        let failedCalls = 0;
        let totalDuration = 0;
        const statusCounts = {};

        stats.forEach(stat => {
          const count = parseInt(stat.count) || 0;
          const avgDuration = parseFloat(stat.avgDuration) || 0;
          
          totalCalls += count;
          totalDuration += avgDuration * count;
          
          statusCounts[stat.status] = count;
          
          if (stat.status === 'completed') {
            completedCalls = count;
          } else if (stat.status === 'failed') {
            failedCalls = count;
          }
        });

        const averageDuration = totalCalls > 0 ? totalDuration / totalCalls : 0;

        // Prepare status distribution for chart
        const statusDistribution = Object.entries(statusCounts).map(([status, count]) => ({
          status,
          count
        }));

        setMetrics({
          totalCalls,
          completedCalls,
          failedCalls,
          averageDuration,
          statusDistribution,
          rawStats: stats
        });
      } else {
        setMetrics({
          totalCalls: 0,
          completedCalls: 0,
          failedCalls: 0,
          averageDuration: 0,
          statusDistribution: [],
          rawStats: []
        });
      }
    } catch (error) {
      console.error('Error fetching metrics:', error);
      setError('Failed to fetch call metrics. Please try again.');
      setMetrics({
        totalCalls: 0,
        completedCalls: 0,
        failedCalls: 0,
        averageDuration: 0,
        statusDistribution: [],
        rawStats: []
      });
    } finally {
      setLoading(false);
    }
  };

  const statusDistributionData = {
    labels: metrics.statusDistribution.map(item => item.status),
    datasets: [
      {
        data: metrics.statusDistribution.map(item => item.count),
        backgroundColor: [
          '#36a2eb',
          '#4bc0c0',
          '#ff6384',
          '#ff9f40',
          '#ffcd56'
        ]
      }
    ]
  };

  const callVolumeData = {
    labels: ['Total', 'Completed', 'Failed', 'Other'],
    datasets: [
      {
        label: 'Call Count',
        data: [
          metrics.totalCalls,
          metrics.completedCalls,
          metrics.failedCalls,
          metrics.totalCalls - metrics.completedCalls - metrics.failedCalls
        ],
        backgroundColor: ['#36a2eb', '#4bc0c0', '#ff6384', '#ffcd56']
      }
    ]
  };

  const successRate = metrics.totalCalls > 0 ? 
    ((metrics.completedCalls / metrics.totalCalls) * 100).toFixed(1) : 0;

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" component="h1">
          📊 QA Metrics & Call Analytics
        </Typography>
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Time Range</InputLabel>
          <Select
            value={timeRange}
            label="Time Range"
            onChange={(e) => setTimeRange(e.target.value)}
          >
            <MenuItem value="week">Last Week</MenuItem>
            <MenuItem value="month">Last Month</MenuItem>
            <MenuItem value="quarter">Last Quarter</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Summary Cards */}
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Calls
              </Typography>
              <Typography variant="h4">
                {metrics.totalCalls}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Completed Calls
              </Typography>
              <Typography variant="h4">
                {metrics.completedCalls}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Success Rate
              </Typography>
              <Typography variant="h4">
                {successRate}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Avg Duration
              </Typography>
              <Typography variant="h4">
                {Math.round(metrics.averageDuration)}s
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Charts */}
        {metrics.statusDistribution.length > 0 ? (
          <>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Call Status Distribution
                </Typography>
                <Box sx={{ height: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <Doughnut 
                    data={statusDistributionData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'bottom'
                        }
                      }
                    }}
                  />
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Call Volume Overview
                </Typography>
                <Box sx={{ height: 300 }}>
                  <Bar 
                    data={callVolumeData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          display: false
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true
                        }
                      }
                    }}
                  />
                </Box>
              </Paper>
            </Grid>
          </>
        ) : (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="textSecondary">
                No call data available for the selected time range
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Try selecting a different time range or check if there are calls in the system.
              </Typography>
            </Paper>
          </Grid>
        )}

        {/* Raw Stats Table */}
        {metrics.rawStats.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Detailed Statistics
              </Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #ddd' }}>
                      <th style={{ padding: '8px', textAlign: 'left' }}>Status</th>
                      <th style={{ padding: '8px', textAlign: 'right' }}>Count</th>
                      <th style={{ padding: '8px', textAlign: 'right' }}>Avg Duration (s)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {metrics.rawStats.map((stat, index) => (
                      <tr key={index} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '8px', textTransform: 'capitalize' }}>{stat.status}</td>
                        <td style={{ padding: '8px', textAlign: 'right' }}>{stat.count}</td>
                        <td style={{ padding: '8px', textAlign: 'right' }}>
                          {stat.avgDuration ? Math.round(parseFloat(stat.avgDuration)) : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default QAMetrics; 